export default {
  loadFailed: "Failed to load tests. Please check your connection and try again.",
  page_header: {
    title: "Tests",
    description: "Manage laboratory test definitions, mappings, and processors.",
  },

  toolbar: {
    refresh: "Refresh",
  },

  table: {
    search_placeholder: "Search tests",
    new_test: "New Test",
    columns: {
      instrument: "Instrument",
      name: "Test Name",
      test_code: "Code / Alias",
      test_code_tooltip:
        "The code the instrument sends to identify this analyte (e.g. 'GLU', 'HGB'). Results with unrecognised codes are silently dropped.",
      modifier: "Modifier",
      modifier_tooltip:
        "Secondary discriminator used when the same test code has multiple result types (e.g. 'CONC' vs 'INTERP'). Leave empty for single-result tests. Used in result reports only, not in orders.",
      direction: "Direction",
      direction_tooltip:
        "Controls how Iris routes data for this test: Bidirectional (orders and results), Orders Only (sends orders, drops results), or Results Only (accepts results, never sends orders).",
      result_type: "Type",
      result_type_tooltip:
        "The format of the result value produced by this test: numeric, text, qualitative (enumerated), etc.",
      scale: "Decimal Places",
      scale_tooltip: "Number of decimal places shown for numeric results from this test.",
      status: "Status",
      created_time: "Date / Time",
      created_time_tooltip: "The date and time this test was created.",
      actions: "Actions",
    },
    empty: {
      title: "No Tests Available",
      description: "There are no test definitions configured yet.",
    },
    no_results: "No tests match '{query}'",
    status_enabled: "Enabled",
    status_disabled: "Disabled",
    collapse_group: "Collapse group",
    expand_group: "Expand group",
    actions: {
      edit: "Edit test",
      delete: "Delete test",
    },
  },

  editor: {
    title_create: "Add Test",
    title_edit: "Edit Test",
    subtitle_create: "Configure a new test definition for the selected instrument",
    subtitle_instrument: "Instrument",
    subtitle_test: "Test",
    add: "Add Test",
    save: "Save Changes",
    reset: "Reset",
    cancel: "Cancel",

    tabs: {
      basic_data: "Basic Data",
      mappings: "LIS Mappings",
      processors: "Processors",
    },

    fields: {
      instrument: "Instrument",
      name: "Test Name",
      test_code: "Code",
      modifier: "Modifier",
      direction: "Direction",
      result_type: "Result Type",
      scale: "Decimal Places",
      is_filter: "Filter Test",
      is_filter_on_title: "Filters Results",
      is_filter_off_title: "Does Not Filter Results",
      is_disabled: "Disabled",
      is_disabled_on_title: "Test Disabled",
      is_disabled_off_title: "Test Active",
      activate: "Activate",
      deactivate: "Deactivate",
      modifier_placeholder: "e.g. STAT",
      scale_placeholder: "e.g. 2",
    },

    hints: {
      instrument: "The instrument this test is associated with.",
      name: "A human-readable name for this test definition.",
      test_code: "The instrument code for this test.",
      modifier: "An additional modifier to distinguish variants of the same test code. e.g. alias*modifier",
      direction: "Whether the test is only ordered, only produced, or both.",
      result_type: "The data type of the result value: quantitative, text, or other.",
      scale: "Number of digits after the decimal point.",
      is_filter_on: "Results with this test code will be held for filtering",
      is_filter_off: "Results with this test will pass through without filtering",
      is_disabled_on: "The test is disabled and will not accept new orders or results",
      is_disabled_off: "The test is active and will accept orders and results",
    },

    validation: {
      test_code_required: "Test code is required",
      name_required: "Test name is required",
      test_code_max_length: "Test code must be 50 characters or less",
      test_code_duplicate: "A test with this code already exists for this instrument",
      name_max_length: "Name must be 255 characters or less",
      modifier_max_length: "Modifier must be 50 characters or less",
      scale_min: "Scale must be at least 0",
      scale_max: "Scale must be at most 10",
    },
  },

  directions: {
    bidirectional: "Bidirectional",
    orders_only: "Orders Only",
    results_only: "Results Only",
  },

  result_types: {
    quantitative: "Quantitative",
    semiquantitative: "Semiquantitative",
    qualitative: "Qualitative",
    text: "Text",
    ratio: "Ratio",
    predefined_list: "Predefined List",
    arbitrary: "Arbitrary",
    antibiotic_susceptibility: "Antibiotic Susceptibility",
    image: "Image",
  },

  direction_hints: {
    bidirectional: "Orders and results — used for both ordering and receiving results",
    results_only: "Results only — never sent as an order to the instrument",
    orders_only: "Orders only — never returns results",
  },

  result_type_hints: {
    quantitative: "Quantitative result — numeric value, e.g. 3, 4.5",
    semiquantitative: "Semiquantitative result — grades, levels or ranges",
    qualitative: "Qualitative result — Positive, Negative, Gray Zone",
    text: 'Text result — free-form text, e.g. "Red", "Pale"',
    ratio: "Result as a ratio, e.g. 1:2, 1:4",
    predefined_list: "Result from a predefined list of values",
    arbitrary: 'Arbitrary result — symbols or notations, e.g. "+", "2+", "-"',
    antibiotic_susceptibility: "Antibiotic susceptibility — e.g. S, I, R",
    image: "Image — binary data",
  },

  mappings: {
    title: "Mappings",
    add: "Add Mapping",
    edit: "Edit mapping",
    done: "Done",
    columns: {
      mapping_key: "LIS ID",
      lis_name: "LIS Name",
      created_time: "Date / Time",
      actions: "Actions",
    },
    add_subtitle: "Configure a new mapping for this test.",
    subtitle_test: "Test Code",
    subtitle_id: "Test ID",
    subtitle_created: "Created",
    subtitle_lis_id: "LIS ID",
    subtitle_test_code_tooltip: "Test code used in LIS orders",
    subtitle_id_tooltip: "System ID of the parent test",
    subtitle_lis_id_tooltip: "LIS key that routes results to this test",
    subtitle_created_tooltip: "When this mapping was created",
    date_created: "Created on",
    date_modified: "Last modified on",
    hints: {
      mapping_key: "The unique ID used by the LIS to identify this test.",
      lis_name: "The display name used by the LIS for this test.",
    },
    empty: "No mappings defined",
    delete_confirm: {
      title: "Remove Mapping",
      message: "This mapping will be removed. The change is applied when you save the test.",
    },
    validation: {
      mapping_key_required: "LIS ID is required",
      mapping_key_too_long: "LIS ID must be 100 characters or fewer",
      mapping_key_duplicate: "A mapping with this key already exists",
      lis_name_required: "LIS name is required",
      lis_name_too_long: "LIS name must be 255 characters or fewer",
    },
    unsaved_dialog_title: "Unsaved Changes",
    unsaved_save_and_leave: "Save & Continue",
    unsaved_discard: "Discard Changes",
    unsaved_stay: "Stay",
  },

  processors: {
    title: "Processors",
    add: "Add Processor",
    edit: "Edit processor",
    done: "Done",
    columns: {
      rank: "Rank",
      active: "Active",
      status: "Status",
      content: "Formula",
      created_time: "Date / Time",
      actions: "Actions",
    },
    status_active: "Active",
    status_inactive: "Inactive",
    add_subtitle: "Configure a new processor for this test.",
    subtitle_rank_tooltip: "Execution order — lower rank runs first",
    subtitle_type_tooltip: "Processor type (script, numeric, or text)",
    subtitle_status_tooltip: "Whether this processor runs during result processing",
    date_created: "Created on",
    date_modified: "Last modified on",
    hints: {
      rank: "Execution order — lower rank runs first.",
      active: "When disabled, this processor is skipped during result processing.",
      active_on: "This processor is active and will run during result processing",
      active_off: "This processor is disabled and will be skipped during result processing",
      content: "JavaScript expression evaluated against the result object.",
    },
    empty: "No processors defined",
    delete_confirm: {
      title: "Remove Processor",
      message: "This processor will be removed. The change is applied when you save the test.",
    },
    validation: {
      rank_min: "Rank must be at least 1",
      rank_duplicate: "A processor with this rank already exists",
      then_action_required: "Add at least one action in 'Then'",
      then_text_value_required: "Text value is required",
    },
    unsaved_dialog_title: "Unsaved Changes",
    unsaved_save_and_leave: "Save & Continue",
    unsaved_discard: "Discard Changes",
    unsaved_stay: "Stay",
    types: {
      js: "JavaScript Script",
      num: "Numeric Conditions",
      txt: "Text Conditions",
    },
    type_label: "Processor Type",
    type_hint: "Select the type of processing logic for this processor.",
    script_label: "Script (JavaScript)",
    when_label: "When",
    then_label: "Then",
    comparison_label: "Comparison",
    value_label: "Value",
    value2_label: "To",
    then_text_value_label: "Text Value",
    then_numeric_prefix_label: "Numeric Prefix",
    then_numeric_value_label: "Numeric Value",
    then_to_be_filtered_label: "Filter from LIS",
    then_to_be_filtered_hint: "When this condition is met, the result will be filtered out and not sent to LIS",
    then_to_be_filtered_on_title: "Filtered — not sent to LIS",
    then_to_be_filtered_off_title: "Not filtered — will be sent to LIS",
    then_to_be_filtered_on_hint: "When this condition is met, the result will be filtered out and not sent to LIS",
    then_to_be_filtered_off_hint: "The result will be sent to LIS normally",
    then_text_value_hint: "Override the text label shown for this result",
    then_numeric_prefix_hint: "Prefix shown before the numeric value, e.g. '>' or '<'",
    then_numeric_value_hint: "Override the numeric value stored for this result",
    flags_label: "Flags",
    flags_hint: "Flags are attached to the result when this condition matches",
    add_flag: "Add Flag",
    no_flags: "No flags defined",
    flag_number: "Flag {n}",
    flag_code_label: "Flag Code",
    flag_value_label: "Flag Description",
    flag_level_label: "Flag Level",
    when_values_label: "Match Values",
    add_when_value: "Add Value",
    no_when_values: "No match values defined",
    when_value_number: "Value {n}",
    js_name_label: "Processor Name",
    js_name_hint: "Optional human-readable label for this script processor",
    when_exclusive_hint: "Less than and greater than are exclusive — the boundary value itself is not matched",
    when_between_hint: "Between is inclusive — both boundary values are matched",
    txt_when_hint: "Type a value and press Enter or click + to add it",
    txt_isnull_hint: "Applies when the text result is empty or null",
    comparisons: {
      less_than: "Less than",
      equal: "Equal",
      greater_than: "Greater than",
      is_null: "Is null",
      between: "Between",
      contains: "Contains",
      starts_with: "Starts with",
      ends_with: "Ends with",
    },
    flag_levels: {
      info: "Info",
      warning: "Warning",
      error: "Error",
      critical: "Critical",
    },
    summary: {
      filtered: "filtered from LIS",
      prefix: "prefix",
      flag: "flag",
      js_script: "JS script",
    },
    preview: {
      value_var: "value",
      text_var: "text",
    },
  },

  delete_dialog: {
    title: "Confirm Deletion",
    subtitle: "You are about to delete test: {name}",
    subtitle_test: "Test",
    subtitle_id: "Test ID",
    subtitle_name_tooltip: "Test display name",
    subtitle_id_tooltip: "System ID of the test",
    warning: "The test and all associated data will be permanently deleted.",
    has_related_items:
      "This test cannot be deleted because it has related mappings or processors. Please remove them first from the respective tabs.",
    confirm: "Delete Test",
    cancel: "Cancel",
  },

  snackbar: {
    create_success: "Test created successfully",
    update_success: "Test updated successfully",
    delete_success: "Test deleted successfully",
    create_failed: "Failed to create test",
    update_failed: "Failed to update test",
    delete_failed: "Failed to delete test",
    fetch_failed: "Failed to load tests",
    mapping_added: "Mapping added",
    mapping_updated: "Mapping updated",
    mapping_removed: "Mapping removed",
    mapping_add_failed: "Failed to add mapping",
    mapping_remove_failed: "Failed to remove mapping",
    processor_added: "Processor added",
    processor_updated: "Processor updated",
    processor_removed: "Processor removed",
    processor_add_failed: "Failed to add processor",
    processor_remove_failed: "Failed to remove processor",
    reset_success: "Basic data restored to last saved state",
    reset_failed: "Failed to reload saved state",
  },
};
